import { FC } from "react";

import { Checkbox, useToast } from "@hightouchio/ui";
import { Controller, useForm } from "react-hook-form";

import { ResourcePermissionGrant } from "src/graphql";
import useHasPermission from "src/hooks/use-has-permission";
import { Button } from "src/ui/button";
import { Field } from "src/ui/field";
import { Input } from "src/ui/input";
import { Modal } from "src/ui/modal";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; private: boolean }) => Promise<void>;
  loading: boolean;
};

export const CreateViewModal: FC<Readonly<Props>> = ({ isOpen, onClose, onSave, loading }) => {
  const { toast } = useToast();
  const { register, control, handleSubmit, watch, reset } = useForm({ defaultValues: { name: "", private: true } });

  const name = watch("name");

  const workspaceCreatePermission = useHasPermission([{ resource: "workspace", grants: [ResourcePermissionGrant.Create] }]);

  return (
    <Modal
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!name}
            loading={loading}
            onClick={handleSubmit(async (data) => {
              await onSave(data);
              reset();
              onClose();

              toast({
                id: "create-view",
                title: `View "${data.name}" was created`,
                variant: "success",
              });
            })}
          >
            Save view
          </Button>
        </>
      }
      isOpen={isOpen}
      sx={{ width: "500px" }}
      title="Save view"
      onClose={onClose}
    >
      <Field label="View name">
        <Input autoFocus {...register("name")} placeholder="Enter a name..." />
      </Field>

      <Controller
        control={control}
        name="private"
        render={({ field }) => (
          <Checkbox
            isChecked={!field.value}
            isDisabled={!workspaceCreatePermission.hasPermission}
            label="Share view with workspace members"
            mt={4}
            onChange={(event) => field.onChange(!event.target.checked)}
          />
        )}
      />
    </Modal>
  );
};
